/**
 * ============================================================================
 * METRIC COMMENTS TYPES — the callout / comment / reply contract.
 * ============================================================================
 *
 * Ported from the legacy CTS UI (ADSProjectPortalUI/types/metric-callouts),
 * adapted to the HILT model: a cell is addressed by `metricKey` +
 * `reportingPeriod` under a (dashboardId, reviewType), where CTS used
 * metricCategory + metricName + wbrWeek.
 *
 * The feature has three record kinds on one cell (metricKey × reportingPeriod):
 *   CALLOUT — one curated headline per cell (backend-enforced unique).
 *   COMMENT — free discussion, many per cell.
 *   REPLY   — nested under a comment or callout.
 *
 * Wire shapes mirror the Smithy model; the UI display shapes (Comment,
 * CommentReply, CellCallout) flatten author/body for rendering.
 */

import { DataMode, ReviewType } from 'src/types/metricsApi.types';

export type MetricCommentType = 'CALLOUT' | 'COMMENT' | 'REPLY';

// ──── Wire shapes (mirror the Smithy model) ────

export interface MetricCommentReplyRecord {
  id: string;
  content: string;
  createdAt: string;
  createdByActor: string;
  updatedAt: string;
  updatedByActor: string;
}

export interface MetricCommentRecord {
  id: string;
  metricKey: string;
  /**
   * The network/section the callout belongs to (CTS `metricCategory`).
   * Populated only once the backend adds the dimension to the record.
   */
  metricCategory?: string;
  dashboardId: string;
  reviewType: ReviewType;
  reportingPeriod: string;
  isCallout: boolean;
  content: string;
  createdAt: string;
  createdByActor: string;
  updatedAt: string;
  updatedByActor: string;
  replies: MetricCommentReplyRecord[];
}

export interface ListMetricCommentsInput {
  dashboardId: string;
  reviewType: ReviewType;
  reportingPeriod: string;
  /** Absent = whole period; a single key returns one cell. */
  metricKeys?: string[];
  /** Network filter (CTS `metricCategory`); sent when known, honoured once the model adds it. */
  metricCategory?: string;
  pageSize?: number;
  paginationKey?: string;
}

export interface ListMetricCommentsOutput {
  comments: MetricCommentRecord[];
  paginationKey?: string;
}

export interface PublishMetricCommentInput {
  /** Absent = create; present = update the addressed record. */
  id?: string;
  type: MetricCommentType;
  dashboardId: string;
  reviewType: ReviewType;
  /** Required for CALLOUT / COMMENT create. */
  metricKey?: string;
  /** Network of the target cell (CTS `metricCategory`); sent when known, honoured once the model adds it. */
  metricCategory?: string;
  reportingPeriod: string;
  /** Required for REPLY create — the parent comment/callout id. */
  commentId?: string;
  /** Required on create; ≤ 50000 chars. */
  content?: string;
  /** Update-only soft delete. */
  isDeleted?: boolean;
  /** Author display name — also the row-level authz key server-side. */
  actor: string;
}

export interface PublishMetricCommentOutput {
  id: string;
  createdAt: string;
  updatedAt: string;
}

// ──── UI display shapes (flattened for rendering) ────

export interface CommentReply {
  id: string;
  author: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  author: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  replies: CommentReply[];
}

/** The one curated callout on a cell, if any. */
export interface CellCallout {
  id: string;
  body: string;
  lastEditedBy: string;
  lastEditedAt: string;
}

/** Everything the callout drawer needs for one cell. */
export interface CellComments {
  callout: CellCallout | null;
  comments: Comment[];
}

/** Identifies the cell a callout/comment addresses. */
export interface CellAnchor {
  /** The served dashboard the cell belongs to (spec multi-dashboard R3.1). */
  dashboardId: string;
  metricKey: string;
  reportingPeriod: string;
  /** Snapshot vs live — callouts are authored on the mode being viewed. */
  dataMode: DataMode;
  /**
   * The network/section (CTS `metricCategory`). Carried through the anchor and
   * sent on the wire so callouts become per-network the moment the backend
   * models it; a no-op until then (Coral ignores the unmodeled field).
   */
  metricCategory?: string;
}

/**
 * A metric cell selected for the unified callout panel's Metric tab. Extends
 * the anchor with the display context the panel shows (name/group + the
 * current-vs-prior value card). Value context is present when the selection
 * comes from a grid cell click (the table knows both weeks' values); it is
 * absent when the selection comes from the week list / pager, where the panel
 * only knows the metricKey — those fields are then rendered as "—".
 */
export interface CalloutSelection {
  /** The served dashboard the cell belongs to (spec multi-dashboard R3.1). */
  dashboardId: string;
  metricKey: string;
  reportingPeriod: string;
  dataMode: DataMode;
  metricName: string;
  /** The metric's catalog group (metricGroup) — a display grouping, NOT the network. */
  group?: string;
  /** The network/section the cell belongs to (CTS `metricCategory`), sent on the wire when known. */
  metricCategory?: string;
  weekLabel: string;
  currentValue?: string;
  priorWeekLabel?: string;
  priorValue?: string;
}

/** The bare anchor a CalloutSelection addresses. */
export function selectionAnchor(sel: CalloutSelection): CellAnchor {
  return {
    dashboardId: sel.dashboardId,
    metricKey: sel.metricKey,
    reportingPeriod: sel.reportingPeriod,
    dataMode: sel.dataMode,
    metricCategory: sel.metricCategory,
  };
}

/**
 * One cell's roll-up for the week-level panel: its callout preview (if any)
 * and how many comments it carries. Display name is joined from the catalog
 * by the component — the accessor only knows metricKey.
 */
export interface CellCalloutSummary {
  metricKey: string;
  /** Network of the cell (CTS `metricCategory`); populated when records carry a network. */
  metricCategory?: string;
  reportingPeriod: string;
  /** The callout body, when the cell has one. */
  calloutPreview?: string;
  commentCount: number;
  lastAuthor: string;
  lastUpdatedAt: string;
}
